import { apiRequest } from "util/services";

const FEEDBACK_BASE = "feedback";
const MIN_CONVERSATIONS = 10;

function mapIntentRow(row) {
  if (!row) return row;
  return {
    id: row.id,
    label: row.label,
    pct: row.pct,
    count: row.count,
    deltaPct: row.delta_pct,
  };
}

function mapObjectionRow(row) {
  if (!row) return row;
  return {
    id: row.id,
    label: row.label,
    mentions: row.mentions ?? row.count ?? 0,
    deltaPct: row.delta_pct,
  };
}

function mapAutoInsight(row) {
  if (!row) return row;
  return {
    id: row.id,
    headline: row.headline,
    detail: row.detail,
    topic: row.topic,
  };
}

function mapInsightsPayload(raw) {
  if (!raw) return null;
  return {
    period: raw.period
      ? {
          days: raw.period.days,
          label: raw.period.label,
          start: raw.period.start,
          end: raw.period.end,
        }
      : null,
    conversationCount: raw.conversation_count ?? 0,
    visitorCount: raw.visitor_count ?? 0,
    leadCount: raw.lead_count ?? 0,
    topIntent: raw.top_intent
      ? { label: raw.top_intent.label, pct: raw.top_intent.pct }
      : null,
    negativeSentimentPct: raw.negative_sentiment_pct ?? 0,
    negativeSentimentDeltaPp: raw.negative_sentiment_delta_pp ?? 0,
    confidence: raw.confidence || "low",
    autoInsights: (raw.auto_insights || []).map(mapAutoInsight),
    topIntents: (raw.top_intents || []).map(mapIntentRow),
    sentimentTrend: raw.sentiment_trend || [],
    frictionPoints: (raw.friction_points || []).map(mapIntentRow),
    objections: (raw.objections || []).map(mapObjectionRow),
    deltas: {
      conversationsPct: raw.deltas?.conversations_pct,
      visitorsPct: raw.deltas?.visitors_pct,
      leadsPct: raw.deltas?.leads_pct,
    },
    insightsSource: raw.insights_source,
    computedAt: raw.computed_at,
    cached: Boolean(raw.cached),
    pendingEod: Boolean(raw.pending_eod),
  };
}

/**
 * @returns {Promise<
 *   | { status: 'empty'; conversationCount: number }
 *   | { status: 'ok'; data: ReturnType<typeof mapInsightsPayload> }
 * >}
 */
export async function fetchIntentInsights(botId, { periodDays = 7 } = {}) {
  if (!botId) {
    throw new Error("missing_bot");
  }
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/intent-insights?period_days=${periodDays}`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  if (res?.status === "empty" || res?.status === "pending") {
    return {
      status: res.status,
      conversationCount: res.conversation_count ?? 0,
      minConversations: res.min_conversations ?? MIN_CONVERSATIONS,
      pendingEod: Boolean(res.pending_eod),
      nextRefreshLabel: res.next_refresh_label || "",
      computedAt: res.computed_at || null,
    };
  }
  if (res?.status !== "ok" || !res?.data) {
    throw new Error("load_failed");
  }
  return {
    status: "ok",
    data: mapInsightsPayload(res.data),
    computedAt: res.computed_at,
    cached: Boolean(res.cached),
    pendingEod: Boolean(res.pending_eod),
    nextRefreshLabel: res.next_refresh_label || "",
  };
}

export async function refreshIntentInsights(botId, { periodDays = 7 } = {}) {
  if (!botId) {
    throw new Error("missing_bot");
  }
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/intent-insights/refresh?period_days=${periodDays}`,
    "POST",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  if (res?.status === "empty" || res?.status === "pending") {
    return {
      status: res.status,
      conversationCount: res.conversation_count ?? 0,
      minConversations: res.min_conversations ?? MIN_CONVERSATIONS,
      pendingEod: Boolean(res.pending_eod),
      nextRefreshLabel: res.next_refresh_label || "",
      computedAt: res.computed_at || null,
    };
  }
  if (res?.status !== "ok" || !res?.data) {
    throw new Error("refresh_failed");
  }
  return {
    status: "ok",
    data: mapInsightsPayload(res.data),
    computedAt: res.computed_at,
    cached: Boolean(res.cached),
    pendingEod: Boolean(res.pending_eod),
    nextRefreshLabel: res.next_refresh_label || "",
  };
}

export function formatComputedAt(iso, locale) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch (_) {
    return iso;
  }
}

export { MIN_CONVERSATIONS };

export function formatDeltaPct(value) {
  if (value == null || Number.isNaN(value)) return "—";
  const n = Number(value);
  const arrow = n >= 0 ? "↑" : "↓";
  return `${arrow} ${Math.abs(n)}%`;
}

export function formatDeltaPp(value) {
  if (value == null || Number.isNaN(value)) return "—";
  const n = Number(value);
  const arrow = n >= 0 ? "↑" : "↓";
  return `${arrow} ${Math.abs(n)}pp`;
}
